import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from 'react';
import { getCart, updateCart, addToCart } from 'lib/burokshop';
import type {
    Cart,
    CartItem,
    ProductVariant
} from 'lib/burokshop/types';

type UpdateType = 'plus' | 'minus' | 'delete';

type CartContextType = {
  cart: Cart | undefined;
  updateCart: (newCart: Cart) => void;
  updateCartItem: (merchandiseId: string, updateType: UpdateType) => Promise<void>;
  addCartItem: (variant: ProductVariant) => Promise<void>;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

function calculateItemCost(quantity: number, price: string): string {
  return (Number(price) * quantity).toString();
}

function updateCartItem(
  item: CartItem,
  updateType: UpdateType
): CartItem | null {
  if (updateType === 'delete') return null;

  const newQuantity =
    updateType === 'plus' ? item.quantity + 1 : item.quantity - 1;
  if (newQuantity === 0) return null;

  const singleItemAmount = Number(item.cost.totalAmount.amount) / item.quantity;
  const newTotalAmount = calculateItemCost(
    newQuantity,
    singleItemAmount.toString()
  );

  return {
    ...item,
    quantity: newQuantity,
    cost: {
      ...item.cost,
      totalAmount: {
        ...item.cost.totalAmount,
        amount: newTotalAmount
      }
    }
  };
}

function updateCartTotals(
  lines: CartItem[]
): Pick<Cart, 'totalQuantity' | 'cost'> {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = lines.reduce(
    (sum, item) => sum + Number(item.cost.totalAmount.amount),
    0
  );
  const currencyCode = lines[0]?.cost.totalAmount.currencyCode ?? 'XOF';

  return {
    totalQuantity,
    cost: {
      subtotalAmount: { amount: totalAmount.toString(), currencyCode },
      totalAmount: { amount: totalAmount.toString(), currencyCode },
      totalTaxAmount: { amount: '0', currencyCode }
    }
  };
}

function createEmptyCart(): Cart {
  return {
    id: undefined,
    checkoutUrl: '',
    totalQuantity: 0,
    lines: [],
    cost: {
      subtotalAmount: { amount: '0', currencyCode: 'XOF' },
      totalAmount: { amount: '0', currencyCode: 'XOF' },
      totalTaxAmount: { amount: '0', currencyCode: 'XOF' }
    }
  };
}

export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cart, setCart] = useState<Cart | undefined>(undefined);

  useEffect(() => {
    async function loadCart() {
      try {
        const cartData = await getCart();
        if (cartData) {
          setCart(cartData);
        } else {
          setCart(createEmptyCart());
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        setCart(createEmptyCart());
      }
    }
    loadCart();
  }, []);

  const handleUpdateCart = useCallback((newCart: Cart) => {
    setCart(newCart);
  }, []);

  const handleUpdateCartItem = useCallback(async (merchandiseId: string, updateType: UpdateType) => {
    if (!cart) return;

    const currentItem = cart.lines.find((item) => item.merchandise.id === merchandiseId);
    if (!currentItem) return;

    let updatedCart: Cart;
    if (updateType === 'delete') {
      const remainingLines = cart.lines.filter((item) => item.merchandise.id !== merchandiseId);
      updatedCart = {
        ...cart,
        lines: remainingLines,
        ...updateCartTotals(remainingLines)
      };
    } else {
      const newQuantity = updateType === 'plus' ? currentItem.quantity + 1 : currentItem.quantity - 1;
      if (newQuantity === 0) {
        const remainingLines = cart.lines.filter((item) => item.merchandise.id !== merchandiseId);
        updatedCart = {
          ...cart,
          lines: remainingLines,
          ...updateCartTotals(remainingLines)
        };
      } else {
        const updatedLines = cart.lines
          .map((item) =>
            item.merchandise.id === merchandiseId ? updateCartItem(item, updateType) || item : item
          )
          .filter(Boolean) as CartItem[];

        updatedCart = {
          ...cart,
          lines: updatedLines,
          ...updateCartTotals(updatedLines)
        };
      }
    }

    // Mise à jour optimiste : affichage immédiat, puis sync en arrière-plan
    setCart(updatedCart);

    try {
      const result = await updateCart(
        updatedCart.lines.map((line) => ({
          id: line.id || '',
          merchandiseId: line.merchandise.id,
          quantity: line.quantity
        }))
      );
      setCart(result);
    } catch (error) {
      console.error('Error updating cart:', error);
      const freshCart = await getCart();
      setCart(freshCart || updatedCart);
    }
  }, [cart]);

  const handleAddCartItem = useCallback(async (variant: ProductVariant) => {
    const result = await addToCart([{
      merchandiseId: variant.id,
      quantity: 1
    }]);
    setCart(result);
  }, []);

  const value = useMemo(
    () => ({
      cart,
      updateCart: handleUpdateCart,
      updateCartItem: handleUpdateCartItem,
      addCartItem: handleAddCartItem
    }),
    [cart, handleUpdateCart, handleUpdateCartItem, handleAddCartItem]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}



